import { Delete, Upload, Visibility } from "@mui/icons-material";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { useDialog } from "../../../hooks/UseDialog";
import ExcelPreviewModal from "./ExcelPreviewModal";
import AlertDialog from "../../../components/common/AlertDialog";
import { useTranslation } from "react-i18next";
import { EmployeeIncentivation } from "../../../types/employeeScores";
import { BinaryRewardCalculationInformation, ManualInputRewardCalculationInformation, ScoreRewardCalculationInformation } from "../../../types/actionModule/instance/rewardCalculationInformation";
import { IncentivationDataType } from "../../../types/actionModule/rewardCalculationType/incentivationDataType";
import { ActionModule } from "../../../types/actionModule/actionModule";
import { GeneralButton } from "../../../common/ui/GeneralButton";

interface Props {
    onUpload: (data: EmployeeIncentivation[]) => Promise<void>;
    actionModule: ActionModule;
}

interface IImportData {
    EmployeeNumber: number | string;
    Score?: number | string;
    Reward?: number | string;
    IsFulfilled?: string;
    [key: string]: string | number | undefined;
}

export default function ExcelUploadButton(props: Props) {
    const { t } = useTranslation();
    const [file, setFile] = useState<any>(false);
    const [parsedData, setParsedData] = useState<EmployeeIncentivation[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const previewDialog = useDialog();
    const errorDialog = useDialog();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const getSubtypeData = (data: IImportData): { [key: string]: string | number; } => {
        const { EmployeeNumber, Score, Reward, IsFulfilled, ...rest } = data;

        return Object.fromEntries(
            Object.entries(rest).filter(([_, value]) => value !== undefined)
        ) as { [key: string]: string | number };
    }


    const isImportData = (data: unknown): data is IImportData => {
        const hasEmployeeNumber = (data as IImportData)?.EmployeeNumber !== undefined &&
            (
                typeof (data as IImportData)?.EmployeeNumber === "string" ||
                typeof (data as IImportData)?.EmployeeNumber === "number"
            );

        const scoreType = (data as IImportData)?.Score === undefined ||
            typeof (data as IImportData)?.Score === "string" ||
            typeof (data as IImportData)?.Score === "number";

        const rewardType = (data as IImportData)?.Reward === undefined ||
            typeof (data as IImportData)?.Reward === "string" ||
            typeof (data as IImportData)?.Reward === "number";

        const isFulfilled = (data as IImportData)?.IsFulfilled === undefined ||
            typeof (data as IImportData)?.IsFulfilled === "string";

        return hasEmployeeNumber && scoreType && rewardType && isFulfilled;
    }

    const mapExcelDataToEmployeeIncentivation = (data: unknown): EmployeeIncentivation | undefined => {
        if (!isImportData(data)) {
            return undefined;
        }

        const subTypeData = getSubtypeData(data);

        switch (props.actionModule.rewardCalculationType?.expectedIncentivationDataType) {
            case IncentivationDataType.Binary:
                return ({
                    employeeNumber: data.EmployeeNumber?.toString(),
                    rewardCalculationInformation: {
                        incentivationDataType: IncentivationDataType.Binary,
                        isFulfilled: (data.IsFulfilled?.trim()?.length ?? 0) >= 1
                    } as BinaryRewardCalculationInformation,
                    subTypeData: subTypeData,
                });
            case IncentivationDataType.Score:
                return ({
                    employeeNumber: data.EmployeeNumber?.toString(),
                    rewardCalculationInformation: {
                        incentivationDataType: IncentivationDataType.Score,
                        score: Number(data.Score)
                    } as ScoreRewardCalculationInformation,
                    subTypeData: subTypeData,
                });
            case IncentivationDataType.ManualInput:
                return ({
                    employeeNumber: data.EmployeeNumber?.toString(),
                    rewardCalculationInformation: {
                        incentivationDataType: IncentivationDataType.ManualInput,
                        enteredReward: Math.floor(Number(data.Reward)),
                    } as ManualInputRewardCalculationInformation,
                    subTypeData: subTypeData,
                });
        }

        return undefined;
    }

    const handleOnFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const file = e.target.files[0];
            if (file.name.match(/^.*\.(xls|xlsx|csv)$/)) {
                setFile(file);
                let reader = new FileReader();
                reader.readAsArrayBuffer(file);
                reader.onload = (e) => {
                    const workbook = XLSX.read(e.target!.result, { type: "buffer" });
                    const workbookName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[workbookName];
                    const data = XLSX.utils.sheet_to_json(worksheet, {
                        blankrows: false, rawNumbers: true,
                    }) as IImportData[];

                    if (!data || !data[0]) {
                        errorDialog.handleDialogOpen();
                        handleOnDelete();
                    }
                    else {
                        const employeeScores: EmployeeIncentivation[] = data
                            .map(mapExcelDataToEmployeeIncentivation)
                            .filter(s => s !== undefined) as EmployeeIncentivation[];

                        setParsedData(employeeScores);
                    }
                };
            } else {
                errorDialog.handleDialogOpen();
            }
        }
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleOnDelete = () => {
        setFile(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleOnPreview = () => {
        if (parsedData && parsedData.length > 0) {
            previewDialog.handleDialogOpen();
        }
    };

    const handleOnUpload = async () => {
        if (parsedData) {
            setIsLoading(true);
            await props.onUpload(parsedData);
            setIsLoading(false);
        }
    }

    return <Stack sx={{ width: "400px" }}>
        <ExcelPreviewModal dialog={previewDialog} data={parsedData} />
        <AlertDialog
            dialog={errorDialog}
            alertMessage={t("dialogModals.incorrectFileType")}
        />

        <input
            ref={inputRef}
            id="excel_upload"
            type="file"
            style={{ display: "none" }}
            onChange={handleOnFileChange}
        />

        <Button variant="outlined" sx={{ mb: 2, p: 2 }} onClick={() => inputRef.current?.click()}>
            {
                file
                    ? <Typography
                        sx={{ width: "100%", textTransform: "none", overflow: "clip", textOverflow: "ellipsis" }}>
                        {file.name}
                    </Typography>
                    : <><Upload sx={{ mr: 1 }} /> XLS, XLSX & CSV</>
            }
        </Button>

        {
            file && <>
                <Stack direction="row" sx={{ pt: 1, alignItems: "end", gap: 1 }}>
                    <GeneralButton
                        loading={isLoading}
                        sx={{ flex: 1 }}
                        onClick={handleOnUpload}>
                        Hochladen
                    </GeneralButton>
                    <IconButton onClick={handleOnPreview}>
                        <Visibility />
                    </IconButton>
                    <IconButton onClick={handleOnDelete}>
                        <Delete />
                    </IconButton>
                </Stack>
            </>
        }
    </Stack>
}
